import React from 'react';
import {
  Monitor,
  Computer,
  Settings,
  Utensils,
  ConciergeBell,
  ReceiptText,
  Users,
  CircleDollarSign,
} from '@parrot-software/design-system/icons';

/**
 * Icon name mapping type
 */
export type IconName =
  | 'monitor'
  | 'computer'
  | 'settings'
  | 'utensils'
  | 'concierge-bell'
  | 'ticket'
  | 'users'
  | 'circle-dollar-sign';

/**
 * Icon components mapping
 */
const iconMap: Record<IconName, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  monitor: Monitor,
  computer: Computer,
  users: Users,
  settings: Settings,
  utensils: Utensils,
  'concierge-bell': ConciergeBell,
  ticket: ReceiptText,
  'circle-dollar-sign': CircleDollarSign,
};

/**
 * Dynamic Icon Component
 *
 * Renders an icon based on a string name.
 * Type-safe with autocomplete support for icon names.
 *
 * @param {DynamicIconProps} props - Component properties
 * @returns {React.ReactElement | null} Icon component or null if not found
 *
 * @example
 * <DynamicIcon name="monitor" className="size-5 text-foreground" />
 */

interface DynamicIconProps extends React.SVGProps<SVGSVGElement> {
  /** Icon name from the available icons */
  name: IconName;
}

export const DynamicIcon = ({ name, className, ...props }: DynamicIconProps): React.ReactElement | null => {
  const IconComponent = iconMap[name];

  if (!IconComponent) {
    // console.warn(`Icon "${name}" not found in icon map`);
    return null;
  }

  return <IconComponent className={className} {...props} />;
};
